import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { cancelExport, exportDownloadUrl, listExports, startExport } from '../api/owner'
import type { ExportView } from '../api/ownerTypes'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Spinner } from '../components/icons'
import { DriveMark, DropboxMark, ImmichMark, OneDriveMark } from '../components/brands'
import { plural } from '../lib/format'
import { useEvent } from './eventContext'
import { EventScreen } from './components/EventScreen'

/**
 * Ronde 21D, 21E en 21F — het album exporteren.
 *
 * De bestemmingen uit het ontwerp staan er, maar geen enkele werkt: er is nog
 * geen koppeling met Drive, OneDrive, Dropbox of Immich. Ze zijn uitgeschakeld
 * en zeggen "Binnenkort". Ze weglaten zou de plek waar ze komen onzichtbaar
 * maken; ze aanklikbaar maken zou liegen.
 */
const destinations = [
  { id: 'drive', mark: <DriveMark className="size-5.5" />, name: 'Google Drive' },
  { id: 'onedrive', mark: <OneDriveMark className="size-5.5" />, name: 'OneDrive' },
  { id: 'dropbox', mark: <DropboxMark className="size-5" />, name: 'Dropbox' },
  { id: 'immich', mark: <ImmichMark className="size-5.5" />, name: 'Immich' },
]

export function EventExportPage() {
  const { subject } = useEvent()
  const queryClient = useQueryClient()

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stopping, setStopping] = useState(false)

  const exports = useQuery({
    queryKey: ['owner', 'exports', subject.id],
    queryFn: () => listExports(subject.id),

    // Zolang er iets loopt elke twee seconden opnieuw. Geen websocket: dit
    // scherm staat hooguit een paar minuten open en een tweede kanaal openen
    // voor een voortgangsbalk is meer techniek dan het waard is.
    refetchInterval: (query) => (query.state.data?.current ? 2000 : false),
  })

  const current = exports.data?.current ?? null
  const previous = exports.data?.previous ?? []

  /** De nieuwste export die nog te downloaden is. */
  const ready = previous.find((job) => job.downloadReady) ?? null

  async function begin() {
    setBusy(true)
    setError(null)

    try {
      await startExport(subject.id)
      await queryClient.invalidateQueries({ queryKey: ['owner', 'exports', subject.id] })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Het exporteren starten lukte niet.')
    } finally {
      setBusy(false)
    }
  }

  async function stop() {
    setStopping(false)

    if (!current) {
      return
    }

    await cancelExport(subject.id, current.id)
    await queryClient.invalidateQueries({ queryKey: ['owner', 'exports', subject.id] })
  }

  const summary = exports.data
    ? `${plural(exports.data.photoCount, 'foto', "foto's")} en ${plural(
        exports.data.videoCount,
        'video',
        `video's`,
      )}, ${formatBytes(exports.data.totalBytes)}.`
    : '…'

  return (
    <>
      <EventScreen
        title="Exporteren"
        eventTitle={subject.title}
        desktopSubtitle={`${summary} Originele kwaliteit, gesorteerd per gast.`}
      >
        {exports.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner className="text-accent size-6" />
          </div>
        ) : (
          <div className="lg:max-w-[760px]">
            {/* Op mobiel staat de samenvatting boven de lijst; op desktop naast
                de titel, en die is hierboven al meegegeven. */}
            <p className="text-ink-soft mb-5 text-[13px] lg:hidden">{summary}</p>

            {/* Staat er een zip klaar, dan is dat het eerste wat je ziet. Hij
                stond eerder alleen onderaan bij "Eerder", en dan zoek je naar
                het ding waar je net op gewacht hebt. */}
            {!current && ready && (
              <div className="rounded-card border-accent/40 bg-notice mb-6 border p-4.5 lg:p-5">
                <p className="text-[15px] font-semibold lg:text-base">Je zip staat klaar</p>
                <p className="text-ink-soft mt-1 text-[13px] lg:text-sm">
                  {ready.sizeBytes === null ? '' : `${formatBytes(ready.sizeBytes)} · `}
                  {ready.doneItems} bestanden
                  {ready.expiresAtUtc && ` · nog te halen tot ${onlyDate(ready.expiresAtUtc)}`}
                </p>

                <a
                  href={exportDownloadUrl(subject.id, ready.id)}
                  className="bg-accent text-accent-ink mt-4 inline-flex h-11.5 items-center rounded-full px-6 text-[15px] font-semibold transition hover:brightness-95"
                >
                  ⤓ Downloaden
                </a>
              </div>
            )}

            {current ? (
              <RunningCard
                subject={current}
                onStop={() => setStopping(true)}
                eventTitle={subject.title}
              />
            ) : (
              <>
                <SectionLabel>Overzetten naar</SectionLabel>

                <div className="flex flex-col gap-2">
                  {destinations.map((destination) => (
                    <button
                      key={destination.id}
                      type="button"
                      disabled
                      className="rounded-notice border-line-card bg-surface flex w-full items-center gap-3.25 border p-3.5 text-left"
                    >
                      {/* Het merk staat op de kaartkleur en niet op `tint`:
                          die accentkleur is van het event en gaat vechten met
                          zes kleuren die niet van ons zijn. En geen `opacity`
                          over de knop, want dat maakt een logo vuil in plaats
                          van rustig — "Binnenkort" zegt het al. */}
                      <span className="border-line-row bg-surface-sunk flex size-10 shrink-0 items-center justify-center rounded-[11px] border">
                        {destination.mark}
                      </span>
                      <span className="flex-1">
                        <span className="text-ink-muted block text-[15px] font-semibold">
                          {destination.name}
                        </span>
                        <span className="text-ink-faint mt-0.5 block text-[13px]">Binnenkort</span>
                      </span>
                      <span aria-hidden="true" className="text-ink-ghost text-lg">
                        ›
                      </span>
                    </button>
                  ))}
                </div>

                <SectionLabel className="mt-7">Of downloaden</SectionLabel>

                <button
                  type="button"
                  onClick={() => void begin()}
                  disabled={busy || exports.data?.totalItems === 0}
                  className="rounded-notice border-line-card bg-surface hover:border-line-warm hover:bg-notice flex w-full items-center gap-3.25 border p-3.5 text-left transition-colors disabled:opacity-55 disabled:hover:bg-transparent"
                >
                  <span className="bg-tint text-accent flex size-10 shrink-0 items-center justify-center rounded-[11px] text-[17px]">
                    {busy ? <Spinner className="size-4" /> : '⤓'}
                  </span>
                  <span className="flex-1">
                    <span className="block text-[15px] font-semibold">Als zip</span>
                    <span className="text-ink-soft mt-0.5 block text-[13px]">
                      {exports.data?.totalItems === 0
                        ? 'Er staat nog niets in dit album'
                        : 'We mailen een link als hij klaar is'}
                    </span>
                  </span>
                  <span aria-hidden="true" className="text-ink-ghost text-lg">
                    ›
                  </span>
                </button>
              </>
            )}

            {error && (
              <p role="alert" className="text-accent mt-4 text-sm">
                {error}
              </p>
            )}

            <p className="text-ink-faint mt-5 text-[13px] leading-[1.5] text-pretty">
              Alles gaat in originele kwaliteit, gesorteerd per gast. Het album zelf blijft staan.
            </p>

            {previous.length > 0 && (
              <>
                <SectionLabel className="mt-8">Eerder</SectionLabel>

                <div className="rounded-card border-line-card bg-surface overflow-hidden border">
                  {previous.map((job, index) => (
                    <PreviousRow
                      key={job.id}
                      job={job}
                      eventId={subject.id}
                      first={index === 0}
                      onRetry={() => void begin()}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </EventScreen>

      <ConfirmDialog
        open={stopping}
        title="Exporteren stoppen?"
        detail="Wat er al ingepakt is gaat verloren. Je album verandert niet en je kan later opnieuw beginnen."
        confirmLabel="Ja, stoppen"
        cancelLabel="Laat lopen"
        onConfirm={() => void stop()}
        onCancel={() => setStopping(false)}
      />
    </>
  )
}

/** Ronde 21E — wat er nu gebeurt, met de voortgang erin. */
/**
 * Wat er nu gebeurt, terwijl het gebeurt.
 *
 * Zodra er een export loopt is dit het scherm. De bestemmingen en de knop om er
 * een te starten verdwijnen zolang hij bezig is — er valt niets te kiezen, er
 * valt alleen te wachten — en dan hoort het enige wat er wél verandert het
 * grootste ding op de pagina te zijn.
 *
 * De opsplitsing per soort staat er met opzet als twee regels en niet als
 * "31 van 54 bestanden". Een album is voor de meeste mensen foto's mét een paar
 * video's ertussen, en die video's zijn traag: staat de balk lang stil, dan is
 * "3 van 6 video's" het antwoord op waarom.
 */
function RunningCard({
  subject,
  eventTitle,
  onStop,
}: {
  subject: ExportView
  eventTitle: string
  onStop: () => void
}) {
  const share = subject.totalItems === 0 ? 0 : subject.doneItems / subject.totalItems
  const percentage = Math.round(share * 100)
  const wachtrij = subject.status === 'Pending'
  const left = remaining(subject)

  return (
    <div className="rounded-card border-accent/40 bg-surface border-[1.5px] p-5 lg:p-6.5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-ink-faint text-[11px] font-semibold tracking-[0.14em] uppercase">
            {wachtrij ? 'In de wachtrij' : 'Bezig met inpakken'}
          </p>
          <p className="mt-1.5 truncate text-[17px] font-semibold lg:text-lg">
            Zip van {eventTitle}
          </p>
        </div>

        {/* Het percentage groot: dat is van een afstand af te lezen, en dit
            scherm staat open terwijl je iets anders doet. */}
        <p className="font-display shrink-0 text-[40px] leading-none lg:text-[52px]">
          {wachtrij ? '—' : `${percentage}%`}
        </p>
      </div>

      <div
        role="progressbar"
        aria-valuenow={wachtrij ? 0 : percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Voortgang van de zip"
        className="bg-line-row mt-4.5 h-2.5 overflow-hidden rounded-full"
      >
        <div
          className={
            'bg-accent h-full rounded-full transition-[width] duration-500 ' +
            (wachtrij ? 'animate-pulse' : '')
          }
          style={{ width: wachtrij ? '100%' : `${Math.max(share * 100, 2)}%` }}
        />
      </div>

      {wachtrij ? (
        <p className="text-ink-soft mt-4 text-sm leading-[1.5]">
          Er loopt nog een export, of we zijn er zo. Dit scherm werkt zichzelf bij.
        </p>
      ) : (
        <dl className="border-line-menu mt-5 grid grid-cols-2 gap-y-3.5 border-t pt-4.5">
          <Progress done={subject.donePhotos} total={subject.totalPhotos} label="foto’s" />
          <Progress done={subject.doneVideos} total={subject.totalVideos} label="video’s" />

          <div className="col-span-2">
            <dt className="text-ink-faint text-xs">Ingepakt</dt>
            <dd className="mt-0.5 text-[15px]">
              {formatBytes(subject.doneBytes)} van {formatBytes(subject.totalBytes)}
              {left && ` · nog ${left}`}
            </dd>
          </div>
        </dl>
      )}

      <p className="text-ink-faint mt-4.5 text-[13px] leading-[1.5] text-pretty">
        Je mag deze pagina sluiten. We mailen je als het klaar is.
      </p>

      <button
        type="button"
        onClick={onStop}
        className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink mt-4 h-11 cursor-pointer rounded-full border px-5 text-[15px] transition-colors"
      >
        Stoppen
      </button>
    </div>
  )
}

/**
 * "28 van 48 foto's". Staat er niets van die soort in het album, dan staat er
 * ook geen regel: "0 van 0 video's" is ruis op een scherm dat je aan het lezen
 * bent omdat je wil weten hoe ver het is.
 */
function Progress({ done, total, label }: { done: number; total: number; label: string }) {
  if (total === 0) {
    return null
  }

  return (
    <div>
      <dt className="text-ink-faint text-xs">{label}</dt>
      <dd className="mt-0.5 text-[15px]">
        <span className="text-[19px] font-semibold">{done}</span> van {total}
      </dd>
    </div>
  )
}

/** Eén regel onder "Eerder" (ronde 21F). */
function PreviousRow({
  job,
  eventId,
  first,
  onRetry,
}: {
  job: ExportView
  eventId: string
  first: boolean
  onRetry: () => void
}) {
  return (
    <div
      className={
        'flex items-center gap-4 px-4 py-3.5 lg:px-5 ' + (first ? '' : 'border-line-menu border-t')
      }
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-medium">
          Zip{job.sizeBytes ? ` · ${formatBytes(job.sizeBytes)}` : ''}
        </p>
        <p className="text-ink-faint mt-0.5 text-[13px]">{describe(job)}</p>

        {job.status === 'Failed' && job.errorMessage && (
          <p className="text-accent mt-1 text-[13px] break-words">{job.errorMessage}</p>
        )}
      </div>

      {job.downloadReady ? (
        <a
          href={exportDownloadUrl(eventId, job.id)}
          className="border-line text-ink-muted hover:border-accent hover:bg-tint hover:text-accent shrink-0 rounded-full border px-4 py-2 text-[13px] transition-colors"
        >
          Downloaden
        </a>
      ) : (
        <button
          type="button"
          onClick={onRetry}
          className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink shrink-0 rounded-full border px-4 py-2 text-[13px] transition-colors"
        >
          Opnieuw
        </button>
      )}
    </div>
  )
}

function SectionLabel({ children, className = '' }: { children: string; className?: string }) {
  return (
    <p
      className={`text-ink-faint mb-3 text-[11px] tracking-[0.18em] uppercase lg:text-xs ${className}`}
    >
      {children}
    </p>
  )
}

/**
 * "± 6 min". Uit wat er al gedaan is en hoe lang dat duurde; voor het eerste
 * item valt er nog niets te zeggen en dan staat er niets.
 */
function remaining(job: ExportView): string {
  if (job.doneItems === 0) {
    return ''
  }

  const elapsed = (Date.now() - new Date(job.createdUtc).getTime()) / 1000
  const perItem = elapsed / job.doneItems
  const left = Math.round((perItem * (job.totalItems - job.doneItems)) / 60)

  return left <= 1 ? '± 1 min' : `± ${left} min`
}

function describe(job: ExportView): string {
  const when = job.finishedAtUtc ?? job.createdUtc
  const stamp = new Date(when).toLocaleString('nl-BE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

  if (job.status === 'Failed') {
    return `Mislukt · ${stamp}`
  }

  if (job.status === 'Cancelled') {
    return `Gestopt · ${stamp}`
  }

  if (job.expiresAtUtc && new Date(job.expiresAtUtc) < new Date()) {
    return `Verlopen · ${stamp}`
  }

  return stamp
}

/** "4,8 GB". Met een komma, want dit is een Nederlandse app. */
function formatBytes(bytes: number): string {
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB']

  let value = bytes
  let unit = 0

  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }

  return unit === 0
    ? `${bytes} bytes`
    : `${value.toFixed(value < 10 ? 1 : 0).replace('.', ',')} ${units[unit]}`
}

/** "31 aug" — voor de regel over hoe lang een zip nog te halen is. */
function onlyDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' })
}
